import React, { useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../config/authConfig';
import { ScimAuthService } from '../services/ScimAuthService';
import { stashHashRouteForMsalRedirect } from '../utils/msalRedirectUtils';
import { isMsalConfigured, logMsalConfigDiagnostics } from '../utils/envUtils';
import { debugLog } from '../utils/debugLog';

/**
 * Component that handles automatic token refresh when the app comes back from sleep
 * or when the page becomes visible again after being hidden.
 */
export const AuthRefreshHandler: React.FC = () => {
    const { instance, accounts } = useMsal();
    const lastCheckTimeRef = useRef<number>(Date.now());
    const isCheckingRef = useRef<boolean>(false);
    const msalEnabled = isMsalConfigured();

    useEffect(() => {
        logMsalConfigDiagnostics('AuthRefreshHandler effect mount', { msalEnabled, accountCount: accounts.length });
        if (!msalEnabled) return;
        // Check token validity when page becomes visible again
        const handleVisibilityChange = async () => {
            if (document.visibilityState === 'visible') {
                const timeSinceLastCheck = Date.now() - lastCheckTimeRef.current;
                // Only check if it's been more than 5 minutes since last check
                if (timeSinceLastCheck > 5 * 60 * 1000 && accounts.length > 0 && !isCheckingRef.current) {
                    logMsalConfigDiagnostics('AuthRefreshHandler visibility check', { timeSinceLastCheck, accountCount: accounts.length });
                    isCheckingRef.current = true;
                    try {
                        debugLog('🔄 AuthRefreshHandler: Page visible, checking token validity...');
                        // Try to get a token - this will fail if refresh token is expired
                        await ScimAuthService.getAuthToken();
                        debugLog('✅ AuthRefreshHandler: Token is valid');
                        lastCheckTimeRef.current = Date.now();
                    } catch (error) {
                        console.warn('⚠️ AuthRefreshHandler: Token check failed, may need re-authentication:', error);
                        // If it's an authentication required error, trigger login
                        if (error instanceof Error && error.message === 'AUTHENTICATION_REQUIRED') {
                            // Clear the current hash before redirecting to avoid redirect loop
                            stashHashRouteForMsalRedirect();
                            
                            debugLog('🔄 AuthRefreshHandler: Triggering login redirect...');
                            instance.loginRedirect(loginRequest).catch((loginError) => {
                                console.error('❌ AuthRefreshHandler: Failed to trigger login redirect:', loginError);
                            });
                        }
                    } finally {
                        isCheckingRef.current = false;
                    }
                }
            }
        };

        // Check token validity when window regains focus (e.g., after sleep)
        const handleFocus = async () => {
            const timeSinceLastCheck = Date.now() - lastCheckTimeRef.current;
            // Only check if it's been more than 5 minutes since last check
            if (timeSinceLastCheck > 5 * 60 * 1000 && accounts.length > 0 && !isCheckingRef.current) {
                logMsalConfigDiagnostics('AuthRefreshHandler focus check', { timeSinceLastCheck, accountCount: accounts.length });
                isCheckingRef.current = true;
                try {
                    debugLog('🔄 AuthRefreshHandler: Window focused, checking token validity...');
                    // Try to get a token - this will fail if refresh token is expired
                    await ScimAuthService.getAuthToken();
                    debugLog('✅ AuthRefreshHandler: Token is valid');
                    lastCheckTimeRef.current = Date.now();
                } catch (error) {
                    console.warn('⚠️ AuthRefreshHandler: Token check failed, may need re-authentication:', error);
                    // If it's an authentication required error, trigger login
                    if (error instanceof Error && error.message === 'AUTHENTICATION_REQUIRED') {
                        // Clear the current hash before redirecting to avoid redirect loop
                        stashHashRouteForMsalRedirect();
                        
                        debugLog('🔄 AuthRefreshHandler: Triggering login redirect...');
                        instance.loginRedirect(loginRequest).catch((loginError) => {
                            console.error('❌ AuthRefreshHandler: Failed to trigger login redirect:', loginError);
                        });
                    }
                } finally {
                    isCheckingRef.current = false;
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, [instance, accounts.length, msalEnabled]);

    // This component doesn't render anything
    return null;
};
